import {
  SidebarCollapseMode,
  SidebarHideBehaviour,
  SidebarHideOpensBehavior,
  SidebarLayoutBehaviour,
  SidebarWidth,
  SidebarTransitionDuration,
  SidebarTimingFunction,
  type SidebarDimensions,
  type SidebarSafeAreas,
  type SidebarAnimations,
  type SidebarConfig,
} from "./Sidebar.types"

// ============================================================================
// VALORES POR DEFECTO
// ============================================================================

/**
 * Dimensiones por defecto del sidebar
 * @constant
 */
export const DEFAULT_DIMENSIONS: SidebarDimensions = {
  collapsedWidth: SidebarWidth.COLLAPSED,
  expandedWidth: SidebarWidth.EXPANDED,
  indicatorHeight: "3rem",
  tooltipDistance: "1rem",
}

/**
 * Áreas seguras por defecto
 * @constant
 */
export const DEFAULT_SAFE_AREAS: SidebarSafeAreas = {
  topPercent: 15,
  bottomPercent: 25,
}

/**
 * Animaciones por defecto
 * @constant
 */
export const DEFAULT_ANIMATIONS: SidebarAnimations = {
  widthTransitionDuration: SidebarTransitionDuration.NORMAL,
  opacityTransitionDuration: SidebarTransitionDuration.FAST,
  timingFunction: SidebarTimingFunction.CUSTOM,
}

/**
 * Configuración por defecto del sidebar
 * @constant
 *
 * @description
 * Los colores se obtienen automáticamente de los tokens CSS de shadcn:
 * - --sidebar: Color de fondo
 * - --sidebar-foreground: Color de texto
 * - --sidebar-primary: Color primario (indicador)
 * - --sidebar-accent: Color de hover
 * - --sidebar-border: Color de bordes
 */
export const DEFAULT_CONFIG: SidebarConfig = {
  defaultOpen: true,
  collapseMode: SidebarCollapseMode.HIDE,
  hideBehaviour: SidebarHideBehaviour.SHOW_INDICATOR,
  hideOpensBehavior: SidebarHideOpensBehavior.COLLAPSED,
  layoutBehaviour: SidebarLayoutBehaviour.FLOATING,
  dimensions: DEFAULT_DIMENSIONS,
  safeAreas: DEFAULT_SAFE_AREAS,
  animations: DEFAULT_ANIMATIONS,
  toggleId: "sidebar-toggle",
  debug: false,
  liquidGlass: {
    enableLiquidGlassV2: false,
    enableChromaticAberration: false,
    enableEdgeGlass3D: false,
    enableBackdropExtension: false,
    enableTooltipItemAnimations: false,
    enableCylinderTitle: false,
  },
}

// ============================================================================
// CONSTANTES DE TECLADO
// ============================================================================

/**
 * Teclas de navegación del sidebar
 * @constant
 */
export const SIDEBAR_KEYBOARD_KEYS = {
  /** Flecha abajo - navegar al siguiente item */
  ARROW_DOWN: "ArrowDown",
  /** Flecha arriba - navegar al item anterior */
  ARROW_UP: "ArrowUp",
  /** Home - ir al primer item */
  HOME: "Home",
  /** End - ir al último item */
  END: "End",
  /** Tab - navegación estándar */
  TAB: "Tab",
  /** Escape - cerrar sidebar o quitar focus */
  ESCAPE: "Escape",
} as const

/**
 * Tipo derivado de las teclas del teclado
 */
export type SidebarKeyboardKey = (typeof SIDEBAR_KEYBOARD_KEYS)[keyof typeof SIDEBAR_KEYBOARD_KEYS]

// ============================================================================
// TIMING FUNCTIONS (CUBIC BEZIER)
// ============================================================================

/**
 * Timing functions perfectas para animaciones suaves
 * @constant
 */
export const SIDEBAR_TIMING_FUNCTIONS = {
  /** Ease-out perfecto para elevación de iconos */
  EASE_OUT_PERFECT: "cubic-bezier(0.4, 0, 0.2, 1)",
  /** Ease-in-out suave */
  EASE_IN_OUT_SMOOTH: "cubic-bezier(0.45, 0, 0.55, 1)",
  /** Spring suave */
  SPRING_SOFT: "cubic-bezier(0.34, 1.56, 0.64, 1)",
} as const

// ============================================================================
// DATA ATTRIBUTES
// ============================================================================

/**
 * Atributos data-* para styling CSS y selección de elementos
 * @constant
 */
export const SIDEBAR_DATA_ATTRIBUTES = {
  /** data-open - indica si el sidebar está abierto */
  OPEN: "data-open",
  /** data-collapsed - indica si el sidebar está colapsado */
  COLLAPSED: "data-collapsed",
  /** data-focused - indica si un elemento está enfocado */
  FOCUSED: "data-focused",
  /** data-hovered - indica si el mouse está sobre un elemento */
  HOVERED: "data-hovered",
  /** data-disabled - indica si un elemento está deshabilitado */
  DISABLED: "data-disabled",
  /** data-sidebar-item - marca un item del sidebar para navegación */
  ITEM: "data-sidebar-item",
  /** data-active - indica si un toggle/botón está activo */
  ACTIVE: "data-active",
  /** data-visible - indica si un elemento es visible */
  VISIBLE: "data-visible",
} as const

// ============================================================================
// CLASES CSS BASE (TAILWIND)
// ============================================================================

/**
 * Clases Tailwind reutilizables para componentes del sidebar
 * @constant
 */
export const SIDEBAR_TAILWIND_CLASSES = {
  /** Clases base para el wrapper */
  wrapper: "relative",

  /** Clases base para el nav */
  nav: "relative h-screen border-r",

  /** Clases base para el indicador */
  indicator: "absolute left-0 rounded-r-full pointer-events-none",

  /** Clases base para el botón toggle */
  toggleButton: "block w-6 h-6 rounded-full cursor-pointer flex items-center justify-center transition-colors",

  /** Clases para el wrapper del botón toggle */
  toggleButtonWrapper: "absolute top-6 z-30",

  /** Clases base para el contenedor de items */
  content: "flex flex-col items-center",

  /** Clases base para áreas seguras */
  safeArea: "flex items-center justify-center",

  /** Clases de borde debug */
  debugBorder: "border-dashed",

  /** Clases de focus ring (accesibilidad) */
  focusRing: "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",

  /** Clases SR-only (screen reader only) */
  srOnly: "sr-only",
} as const

/**
 * Tipo derivado de las clases Tailwind
 */
export type SidebarTailwindClass = (typeof SIDEBAR_TAILWIND_CLASSES)[keyof typeof SIDEBAR_TAILWIND_CLASSES]

// ============================================================================
// PATRONES DE DEBUG
// ============================================================================

/**
 * Patrones visuales para modo debug
 * @constant
 */
export const SIDEBAR_DEBUG_PATTERNS = {
  /** Patrón de rayas para áreas seguras */
  safeArea:
    "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,0.03) 10px, rgba(255,255,255,0.03) 20px)",

  /** Fondo semi-transparente para área de contenido */
  contentArea: "rgba(0, 0, 0, 0.5)",

  /** Color de borde para debug */
  borderColor: "rgb(34 197 94)", // emerald-500

  /** Opacidad de borde debug */
  borderOpacity: "0.3",
} as const

// ============================================================================
// ROLES ARIA
// ============================================================================

/**
 * Roles ARIA para accesibilidad
 * @constant
 */
export const SIDEBAR_ARIA_ROLES = {
  /** Rol para el nav principal */
  navigation: "navigation",

  /** Rol para lista de items */
  list: "list",

  /** Rol para item individual */
  listitem: "listitem",

  /** Rol para botón toggle */
  button: "button",
} as const

// ============================================================================
// LABELS ARIA POR DEFECTO
// ============================================================================

/**
 * Labels ARIA por defecto para accesibilidad
 * @constant
 */
export const SIDEBAR_ARIA_LABELS = {
  /** Label para toggle */
  toggleSidebar: "Alternar sidebar",

  /** Label para navegación principal */
  mainNavigation: "Navegación principal",

  /** Label cuando sidebar está abierto */
  sidebarOpen: "Sidebar abierto",

  /** Label cuando sidebar está cerrado */
  sidebarClosed: "Sidebar cerrado",
} as const

// ============================================================================
// ESTILOS VISUALES
// ============================================================================

/**
 * Configuración de estilo visual por defecto del sidebar
 * @constant
 */
export const DEFAULT_VISUAL_STYLE = {
  marginTop: "0",
  marginBottom: "0",
  marginLeft: "0",
  marginRight: "0",
  height: "100vh",
  borderRadius: "0",
  boxShadow: "none",
} as const

/**
 * Preconfiguraciones de estilo visual para el sidebar
 * @constant
 */
export const SIDEBAR_VISUAL_PRESETS = {
  /** Estilo por defecto: pegado a bordes */
  DEFAULT: {
    marginTop: "0",
    marginBottom: "0",
    marginLeft: "0",
    marginRight: "0",
    height: "100vh",
    borderRadius: "0",
    boxShadow: "none",
  },

  /** Estilo floating: separado y con sombra */
  FLOATING_CARD: {
    marginTop: "1rem",
    marginBottom: "1rem",
    marginLeft: "1rem",
    marginRight: "0",
    height: "calc(100vh - 2rem)",
    borderRadius: "0.75rem",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  },

  /** Estilo moderno: esquinas redondeadas suaves */
  MODERN_ROUNDED: {
    marginTop: "0.5rem",
    marginBottom: "0.5rem",
    marginLeft: "0.5rem",
    marginRight: "0",
    height: "calc(100vh - 1rem)",
    borderRadius: "1rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  },

  /** Estilo minimal: ligera separación */
  MINIMAL_SPACED: {
    marginTop: "0.25rem",
    marginBottom: "0.25rem",
    marginLeft: "0.25rem",
    marginRight: "0",
    height: "calc(100vh - 0.5rem)",
    borderRadius: "0.5rem",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  },
} as const

/**
 * Tipo para los presets de estilo visual
 */
export type SidebarVisualPreset = keyof typeof SIDEBAR_VISUAL_PRESETS

// ============================================================================
// BREAKPOINTS
// ============================================================================

/**
 * Breakpoints para responsive behavior
 * @constant
 */
export const SIDEBAR_BREAKPOINTS = {
  /** Ancho mínimo para mostrar sidebar expandido */
  minExpandedWidth: 768, // md en Tailwind

  /** Ancho mínimo para sidebar colapsado */
  minCollapsedWidth: 640, // sm en Tailwind

  /** Ancho para forzar modo mobile */
  mobileWidth: 480,
} as const

// ============================================================================
// LÍMITES Y VALORES SEGUROS
// ============================================================================

/**
 * Límites y valores seguros para validación
 * @constant
 */
export const SIDEBAR_LIMITS = {
  /** Porcentaje mínimo para safe area */
  minSafeAreaPercent: 0,

  /** Porcentaje máximo para safe area */
  maxSafeAreaPercent: 50,

  /** Número máximo de items recomendado */
  maxRecommendedItems: 20,

  /** Duración mínima de transición (ms) */
  minTransitionDuration: 0,

  /** Duración máxima de transición (ms) */
  maxTransitionDuration: 1000,
} as const

// ============================================================================
// CSS VARIABLES
// ============================================================================

/**
 * Nombres de CSS variables utilizadas
 * @constant
 */
export const SIDEBAR_CSS_VARIABLES = {
  /** Ancho cuando cerrado */
  widthClosed: "--sidebar-width-closed",

  /** Ancho cuando abierto */
  widthOpen: "--sidebar-width-open",

  /** Ancho actual */
  width: "--sidebar-width",

  /** Color de fondo */
  background: "--sidebar-bg",

  /** Color de borde (variable interna para evitar colisión con --sidebar-border de shadcn) */
  borderColor: "--sidebar-border-color",

  /** Color de hover */
  hover: "--sidebar-hover",

  /** Color del indicador */
  indicator: "--sidebar-indicator",

  /** Altura del indicador */
  indicatorHeight: "--sidebar-indicator-height",

  /** Distancia del tooltip al sidebar */
  tooltipDistance: "--sidebar-tooltip-distance",

  /** Comportamiento de layout */
  layoutBehaviour: "--sidebar-layout-behaviour",

  /** Flag para modo inline */
  isInline: "--sidebar-is-inline",

  /** Variables de estilo visual */
  marginTop: "--sidebar-margin-top",
  marginBottom: "--sidebar-margin-bottom",
  marginLeft: "--sidebar-margin-left",
  marginRight: "--sidebar-margin-right",
  sidebarHeight: "--sidebar-height",
  sidebarBorderRadius: "--sidebar-border-radius",
  sidebarBoxShadow: "--sidebar-box-shadow",
} as const
